import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { X, ChevronDown, Loader2 } from 'lucide-react';

// --- 1. Simulated Async API ---
const fetchPropertyTypes = () => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve([
                { id: 1, label: 'Office Space' },
                { id: 2, label: 'Plot' },
                { id: 3, label: 'Apartment' },
                { id: 4, label: 'Villa' },
                { id: 5, label: 'Warehouse' },
                { id: 6, label: 'Penthouse' },
                { id: 7, label: 'Studio' },
                { id: 8, label: 'Retail Shop' },
            ]);
        }, 1500); // 1.5s delay to simulate network
    });
};

const MultiSelectWidget = ({ options: externalOptions, onChange }) => {
    // --- State Management ---
    const [internalOptions, setInternalOptions] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Use external options if provided, otherwise use internal options
    const options = externalOptions || internalOptions;

    // --- Async Option Loading ---
    useEffect(() => {
        if (!externalOptions) {
            const loadData = async () => {
                setIsLoading(true);
                try {
                    const data = await fetchPropertyTypes();
                    setInternalOptions(data);
                } catch (error) {
                    console.error("Failed to load options");
                } finally {
                    setIsLoading(false);
                }
            };
            loadData();
        }
    }, [externalOptions]);

    // --- Notify Parent of Changes ---
    useEffect(() => {
        if (onChange) {
            onChange(selectedItems);
        }
    }, [selectedItems, onChange]);

    // --- Filtering Logic ---
    const filteredOptions = useMemo(() => {
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(inputValue.toLowerCase()) &&
                !selectedItems.find((selected) => selected.id === opt.id)
        );
    }, [options, inputValue, selectedItems]);

    // --- Handlers ---
    const handleSelect = (item) => {
        setSelectedItems([...selectedItems, item]);
        setInputValue('');
        setFocusedIndex(-1);
        inputRef.current?.focus();
    };

    const handleRemove = (itemId) => {
        setSelectedItems(selectedItems.filter((i) => i.id !== itemId));
    };

    const handleClearAll = (e) => {
        e.stopPropagation();
        setSelectedItems([]);
        setInputValue('');
        inputRef.current?.focus();
    };

    // --- Keyboard Accessibility Logic ---
    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'Backspace':
                if (inputValue === '' && selectedItems.length > 0) {
                    handleRemove(selectedItems[selectedItems.length - 1].id);
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen && focusedIndex >= 0 && filteredOptions[focusedIndex]) {
                    handleSelect(filteredOptions[focusedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                inputRef.current?.blur();
                break;
            default:
                break;
        }
    };

    // Close dropdown if clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="w-full max-w-md mx-auto p-10 font-sans">

            {/* Label */}
            <label className="block text-sm font-bold text-gray-700 mb-2">
                Property Sub-Type <span className="text-red-500">*</span>
            </label>

            {/* Main Container */}
            <div
                ref={dropdownRef}
                className="relative"
            >
                <div
                    className={`
            flex flex-wrap items-center gap-2 p-2 min-h-[42px]
            bg-white border rounded-md cursor-text
            ${isOpen ? 'border-green-400 ring-1 ring-green-100' : 'border-gray-300'}
            transition-all duration-200
          `}
                    onClick={() => {
                        setIsOpen(true);
                        inputRef.current?.focus();
                    }}
                >
                    {/* Render Selected Chips */}
                    {selectedItems.map((item) => (
                        <span
                            key={item.id}
                            className="flex items-center gap-1 bg-gray-200 text-gray-700 text-sm px-2 py-1 rounded-md font-medium"
                        >
                            {item.label}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(item.id);
                                }}
                                className="hover:text-red-500 focus:outline-none"
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))}

                    {/* Search Input */}
                    <input
                        ref={inputRef}
                        type="text"
                        className="flex-grow min-w-[50px] outline-none text-sm bg-transparent"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            setIsOpen(true);
                            setFocusedIndex(0);
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsOpen(true)}
                        placeholder={selectedItems.length === 0 ? "Select options..." : ""}
                    />

                    {/* Action Icons */}
                    <div className="flex items-center gap-2 text-gray-400">
                        {/* Clear All Button */}
                        {selectedItems.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="hover:text-gray-600 transition-colors"
                                title="Clear All"
                            >
                                <X size={16} />
                            </button>
                        )}

                        {/* Loading Indicator or Dropdown Arrow */}
                        {isLoading ? (
                            <Loader2 size={16} className="animate-spin text-green-500" />
                        ) : (
                            <div className="border-l border-gray-300 pl-2">
                                <ChevronDown size={16} className={`${isOpen ? 'rotate-180' : ''} transition-transform`} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {isLoading ? (
                            <div className="p-4 text-center text-sm text-gray-500">Loading options...</div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">No options found.</div>
                        ) : (
                            <ul>
                                {filteredOptions.map((option, index) => (
                                    <li
                                        key={option.id}
                                        onClick={() => handleSelect(option)}
                                        className={`
                      px-4 py-2 text-sm cursor-pointer flex items-center justify-between
                      ${index === focusedIndex ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}
                    `}
                                        onMouseEnter={() => setFocusedIndex(index)}
                                    >
                                        {option.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

MultiSelectWidget.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    onChange: PropTypes.func,
};

export default MultiSelectWidget;
